import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_SERIENDRUCK, SERIENDRUCK_TYP } from "../makros";

export const lektionEtiketten: Lesson = {
  id: "umschlaege-etiketten",
  title: "Umschläge, Etiketten und Fertigstellen",
  summary: "Dieselbe Empfängerliste für Adressetiketten – und der letzte Schritt zum fertigen Stapel.",
  durationMinutes: 9,
  learningGoals: [
    "Einen Etiketten-Seriendruck einrichten",
    "Die Regel „Nächster Datensatz“ verstehen",
    "Einen Seriendruck fertigstellen und das Ergebnis richtig speichern",
  ],
  steps: [
    {
      id: "etiketten",
      title: "Adressetiketten",
      explanation: [
        "Für Etiketten beginnt man mit einem neuen, leeren Dokument: „Sendungen“ → " +
          "„Seriendruck starten“ → „Etiketten“. Im Dialog wählst du Hersteller und " +
          "Produktnummer des Etikettenbogens – sie steht auf der Packung. Word legt eine " +
          "Tabelle an, deren Zellen genau den Etiketten entsprechen.",
        "Danach Empfänger verbinden, im ersten Etikett einen Adressblock einfügen und " +
          "„Etiketten aktualisieren“ klicken. Word kopiert das erste Etikett in alle anderen " +
          "und setzt dazwischen jeweils das Feld „Nächster Datensatz“, damit jedes Etikett " +
          "einen anderen Empfänger bekommt.",
        "Umschläge funktionieren genauso über „Seriendruck starten“ → „Umschläge“, nur mit " +
          "einem Adressfeld statt einer Tabelle.",
      ],
      keyPoints: [
        "Neues Dokument → Seriendruck starten → Etiketten → Produktnummer wählen.",
        "Adressblock ins erste Etikett, dann „Etiketten aktualisieren“.",
        "«Nächster Datensatz» sorgt für einen neuen Empfänger je Etikett.",
      ],
      task: {
        instruction:
          "Lege ein neues Dokument an, öffne dort dieses Kurs-Panel und richte einen " +
          "Etiketten-Seriendruck mit verbundener Empfängerliste ein.",
        hint: "Sendungen → Seriendruck starten → Etiketten, Etikett wählen, dann Empfänger auswählen, Adressblock, Etiketten aktualisieren.",
        macro: MAKRO_SERIENDRUCK,
        check: () =>
          pruefeMitMakro(MAKRO_SERIENDRUCK, (bericht) => {
            const typ = bericht.zahl("typ") ?? -1;
            return fromCriteria(
              [
                criterion(`Hauptdokument vom Typ „Etiketten“ (erkannt: ${SERIENDRUCK_TYP[typ] ?? typ})`, typ === 1),
                criterion("Eine Datenquelle ist verbunden", bericht.janein("datenquelle") === true),
                criterion(
                  `Seriendruckfelder im Dokument (gefunden: ${bericht.zahl("felder") ?? 0})`,
                  (bericht.zahl("felder") ?? 0) > 0,
                ),
              ],
              {
                success: "Der Etikettenbogen ist bereit für den Druck.",
                failure: "Der Etiketten-Seriendruck ist noch nicht vollständig.",
                hint: "Das Makro muss in dem Etiketten-Dokument laufen, nicht im Brief.",
              },
            );
          }),
      },
    },
    {
      id: "fertigstellen",
      title: "Fertigstellen und zusammenführen",
      explanation: [
        "„Fertigstellen und zusammenführen“ bietet drei Wege. „Einzelne Dokumente " +
          "bearbeiten“ erzeugt ein neues Dokument mit allen Briefen – ohne Felder, bereit " +
          "zum Durchsehen und Korrigieren einzelner Stellen. „Dokumente drucken“ schickt " +
          "alles direkt an den Drucker. „E-Mail-Nachrichten senden“ verschickt jeden Brief " +
          "als eigene Mail an die Adresse aus der Datenquelle.",
        "Gespeichert wird zweierlei: das Hauptdokument, damit der nächste Seriendruck nur " +
          "noch ein Klick ist, und – wenn nötig – das Ergebnisdokument als Nachweis. " +
          "Korrekturen gehören ins Hauptdokument oder in die Datenquelle, nicht ins " +
          "Ergebnis; sonst sind sie beim nächsten Mal wieder weg.",
      ],
      keyPoints: [
        "Einzelne Dokumente bearbeiten = Ergebnis zum Durchsehen.",
        "Hauptdokument speichern – es ist die Vorlage für das nächste Mal.",
        "Korrekturen ins Hauptdokument oder die Datenquelle, nicht ins Ergebnis.",
      ],
    },
  ],
};
